import { useState } from "react";
import { Header } from "components/atoms/Typography";
import { Table } from "components/molecules/Table";
import ViewTab from "components/atoms/ViewTab";
import { Box } from "@mui/material";

import { useActiveCustomerAccount } from "hooks/useActiveCustomerAccount";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import { Button } from "components/atoms/Button";
import { useAddUsers } from "mutations/Users/useAddUsers";
import { showMutationAlert } from "utils/helpers";
import useAlert from "hooks/useAlert";
import OrganizationInfo from "./OrganizationInfo";
import styles from "./SettingsPage.module.scss";
import { formattedTableViewColumns } from "./formattedTableViewColumns";

function SettingsPage() {
    const { user, activeCustomerAccount, isFetching } = useActiveCustomerAccount();
    const { mutate: mutateAddUsers, isLoading } = useAddUsers();
    const [emails, setEmails] = useState<string>("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const alert = useAlert();

    if (isFetching) {
        return <LoadingSpinner />;
    }

    if (!user || !activeCustomerAccount) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <p>User data could not be loaded.</p>
            </Box>
        );
    }

    const members: BriefUserAccount[] = activeCustomerAccount.members;
    const membersFormatted = members?.map((member) => ({
        userId: member.id,
        key: member.id,
        email: member.email,
        role: member.roles.join(", "),
        isActive: member.hasLoggedIn,
        status: member.hasLoggedIn === true ? "Active" : "Invited",
        action: {
            userId: member.id,
            roles: member.roles,
            email: member.email,
        },
    }));

    const handleAddusers = () => {
        const emailsArray = emails
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email);
        const invalidEmails = emailsArray.filter((email) => !emailRegex.test(email));

        if (invalidEmails.length > 0) {
            const errorMessage = `Invalid email(s): ${invalidEmails.join(", ")}`;
            showMutationAlert(errorMessage, alert);
            return;
        }
        mutateAddUsers(
            { emails: emailsArray },
            {
                onSuccess: () => {
                    setEmails("");
                },
            }
        );
    };

    // test

    return (
        <div>
            <div className={styles.settingsPage}>
                <div className={styles.pageHeader}>
                    <Header size="large">Settings</Header>
                    <div className={`report-tabs ${styles.settingsTabs}`}>
                        <ViewTab name="Organization" onClick={() => {}} selected={true} />
                    </div>
                </div>
                <div className={styles.settingsContent}>
                    <div className="horizontal-form">
                        <h2 className={styles.bottomMargin}>Members ({members?.length || "No member found"})</h2>

                        <form className={styles.formInputWrapper} onSubmit={(e) => e.preventDefault()}>
                            <input
                                className={styles.formInput}
                                type="text"
                                placeholder="Enter emails, comma separated"
                                value={emails}
                                onChange={(e) => setEmails(e.target.value)}
                            />
                            <Button
                                style={{ width: "76px" }}
                                type="primary"
                                disabled={!emails || isLoading}
                                onClick={() => handleAddusers()}
                            >
                                {isLoading ? "Loading..." : "Invite"}
                            </Button>
                        </form>

                        <div className="settingsTable">
                            <Table
                                columns={formattedTableViewColumns}
                                dataSource={membersFormatted}
                                className={styles.settingsTable}
                                pagination={false}
                                defaultOrderBy={{
                                    key: "email",
                                    order: "asc",
                                }}
                            />
                        </div>
                    </div>

                    <OrganizationInfo activeOrganization={activeCustomerAccount} />
                </div>
            </div>
        </div>
    );
}

export default SettingsPage;
